import useSWRMutation from "swr/mutation";
import { ServicesURL } from "@utils/utilsApi";
import { fetcher } from "src/services/config";
import { MethodType } from "src/services/type";
import { formatDateWithMinutes } from "@utils/utilsDate";
import {
  EnrollmentConfigState,
  EnrollmentTargetsState,
  EnrollmentTriggerPayload,
} from "../types";

export const buildCommonConfig = (config: EnrollmentConfigState) => {
  const isFixedDate = config.expirationKind === "fixed-date";
  return {
    isAdmin: true,
    enrollDate: formatDateWithMinutes(new Date()),
    enrollType: config.enrollType,
    forcedExpiration: config.enableExpiration
      ? !!config.forcedExpiration
      : null,
    daysToComplete:
      config.enableExpiration && !isFixedDate ? config.daysToComplete : null,
    expirationDate:
      config.enableExpiration && isFixedDate && config.expirationDate
        ? formatDateWithMinutes(new Date(config.expirationDate)).split(" ")[0]
        : null,
    isExpirationFixed: config.enableExpiration ? isFixedDate : null,
    isExpirationHard: config.enableExpiration
      ? config.expirationType === "hard"
      : null,
    attributesIds: config.attributesIds ?? [],
  };
};

const buildUsersPayload = (
  trigger: EnrollmentTriggerPayload,
  targets: EnrollmentTargetsState,
  config: EnrollmentConfigState,
) => {
  const common = buildCommonConfig(config);
  return targets.users.map((u) => ({
    ...common,
    learningObject: {
      corporateId: Number(trigger.corporateId),
      objectId: trigger.objectId,
    },
    userId: u.id,
    domainId: null,
    massiveDomainId: null,
    massiveGroupId: null,
  }));
};

// Vecchia logica "una sola chiamata massive per tutti i gruppi selezionati".
// Mantenuta commentata per riferimento. La nuova logica fa N chiamate
// separate (una per gruppo) per poter passare il groupType corretto di ciascuno.
// const buildGroupsPayload = (
//   trigger: EnrollmentTriggerPayload,
//   targets: EnrollmentTargetsState,
//   config: EnrollmentConfigState,
// ) => ({
//   ...buildCommonConfig(config),
//   learningObject: {
//     corporateId: Number(trigger.corporateId),
//     forcedEnroll: true,
//     objectId: [trigger.objectId],
//   },
//   groupType: "DYNAMIC", //TODO togliere
//   massiveListGroupId: targets.groups.map((g) => Number(g.id)),
//   massiveListDomainId: null,
// });

const buildSingleGroupPayload = (
  trigger: EnrollmentTriggerPayload,
  group: { id: string | number; groupType?: string },
  config: EnrollmentConfigState,
) => ({
  ...buildCommonConfig(config),
  domainId: null,
  groupType: group.groupType,
  learningObject: {
    corporateId: Number(trigger.corporateId),
    forcedEnroll: true,
    objectId: [trigger.objectId],
  },
  massiveListDomainId: null,
  massiveListGroupId: [Number(group.id)],
});

const buildInitiativesPayload = (
  trigger: EnrollmentTriggerPayload,
  targets: EnrollmentTargetsState,
  config: EnrollmentConfigState,
) => ({
  ...buildCommonConfig(config),
  domainId: null,
  learningObject: {
    corporateId: Number(trigger.corporateId),
    objectId: trigger.objectId,
  },
  massiveListDomainId: targets.initiatives.map((i) => Number(i.id)),
  massiveListGroupId: null,
});

export interface SubmitArgs {
  trigger: EnrollmentTriggerPayload;
  targets: EnrollmentTargetsState;
  config: EnrollmentConfigState;
}

export const useEnrollmentSubmit = () => {
  const { trigger: postUsers, isMutating: isMutatingUsers } = useSWRMutation(
    ServicesURL.postEnrollment,
    (url: string, { arg }: { arg: any[] }) =>
      fetcher<any>(url, MethodType.POST, arg),
  );

  const { trigger: postGroups, isMutating: isMutatingGroups } = useSWRMutation(
    ServicesURL.postGroupEnrollmentMassive,
    (url: string, { arg }: { arg: any }) =>
      fetcher<any>(url, MethodType.POST, arg),
  );

  const { trigger: postInitiatives, isMutating: isMutatingInitiatives } =
    useSWRMutation(
      ServicesURL.postEnrollmentMassive,
      (url: string, { arg }: { arg: any }) =>
        fetcher<any>(url, MethodType.POST, arg),
    );

  const submit = async ({ trigger, targets, config }: SubmitArgs) => {
    // Chiamate sequenziali

    // TODO verificare alternative possibili
    if (targets.users.length > 0) {
      await postUsers(buildUsersPayload(trigger, targets, config));
    }

    for (const g of targets.groups) {
      await postGroups(buildSingleGroupPayload(trigger, g, config));
    }

    if (targets.initiatives.length > 0) {
      await postInitiatives(buildInitiativesPayload(trigger, targets, config));
    }
  };

  return {
    submit,
    isSubmitting: isMutatingUsers || isMutatingGroups || isMutatingInitiatives,
  };
};
